import React, { useState } from 'react';
import { cn } from '../lib/utils';
import {
  Brain,
  Shield,
  Activity,
  Globe,
  Database,
  Users,
  AlertTriangle,
  TrendingUp,
  Package,
  Clock,
  CheckCircle,
  Zap,
  Target,
  MapPin,
  BarChart3,
  FileText,
  Radio,
  Satellite,
  Heart,
  ArrowRight,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  benefits: string[];
  mockupType: 'dashboard' | 'map' | 'analytics' | 'alerts' | 'coordination' | 'audit';
}

const features: Feature[] = [
  {
    id: 'ai-predictions',
    title: 'AI-Powered Predictions',
    description: 'Advanced machine learning models analyze multiple data streams to predict humanitarian crises before they fully develop, enabling proactive response.',
    icon: Brain,
    benefits: [
      'Predict floods, disease outbreaks, and food crises up to 72 hours in advance',
      'Confidence scoring for transparent decision-making',
      'Continuous learning from new data patterns',
      'Multi-source data integration'
    ],
    mockupType: 'dashboard'
  },
  {
    id: 'crisis-mapping',
    title: 'Real-Time Crisis Mapping',
    description: 'Interactive maps showing active crises, affected populations, and resource deployments across regions with live data updates.',
    icon: Globe,
    benefits: [
      'Live visualization of crisis zones',
      'Population density overlays',
      'Resource deployment tracking',
      'Evacuation route planning'
    ],
    mockupType: 'map'
  },
  {
    id: 'resource-optimization',
    title: 'Resource Allocation Engine',
    description: 'Intelligent resource management system that optimizes the distribution of supplies, personnel, and funding based on predicted needs.',
    icon: Package,
    benefits: [
      'Automated supply chain recommendations',
      'Multi-agency resource sharing',
      'Urgency-based prioritization',
      'Inventory tracking and forecasting'
    ],
    mockupType: 'coordination'
  },
  {
    id: 'early-warning',
    title: 'Early Warning System',
    description: 'Multi-channel alert system that notifies stakeholders of emerging threats with customizable severity thresholds and escalation protocols.',
    icon: AlertTriangle,
    benefits: [
      'SMS, email, and push notifications',
      'Configurable alert thresholds',
      'Escalation workflows',
      'Multi-language support'
    ],
    mockupType: 'alerts'
  },
  {
    id: 'data-integration',
    title: 'Multi-Source Data Integration',
    description: 'Seamlessly aggregate data from satellites, weather stations, field reports, social media, and partner organizations into a unified view.',
    icon: Database,
    benefits: [
      'Satellite imagery analysis',
      'Weather data integration',
      'Social media monitoring',
      'Field report aggregation'
    ],
    mockupType: 'analytics'
  },
  {
    id: 'audit-trail',
    title: 'Decision Audit Trail',
    description: 'Comprehensive logging of all decisions with cryptographic validation, ensuring accountability and enabling post-crisis analysis.',
    icon: Shield,
    benefits: [
      'Immutable decision records',
      'Evidence attachment support',
      'Timeline visualization',
      'Compliance reporting'
    ],
    mockupType: 'audit'
  }
];

// Mockup Components
const DashboardMockup = () => (
  <div className="bg-white border-2 border-ink-200 p-4 space-y-4">
    <div className="flex items-center justify-between border-b border-ink-100 pb-3">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 bg-sage-500"></div>
        <span className="text-xs font-mono uppercase tracking-wider text-ink-500">AI Analysis Active</span>
      </div>
      <div className="text-xs font-mono text-ink-400">Last updated: 2 min ago</div>
    </div>

    {/* Prediction Cards */}
    <div className="space-y-3">
      {[
        { type: 'Flood Risk', location: 'Cox\'s Bazar', probability: 78, severity: 'high' },
        { type: 'Disease Outbreak', location: 'Dadaab', probability: 62, severity: 'medium' },
        { type: 'Food Crisis', location: 'Tigray', probability: 85, severity: 'critical' }
      ].map((prediction, i) => (
        <div key={i} className="border border-ink-100 p-3 hover:border-accent-300 transition-colors">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-medium text-sm text-ink-800">{prediction.type}</p>
              <p className="text-xs text-ink-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {prediction.location}
              </p>
            </div>
            <span className={cn(
              'text-xs font-mono px-2 py-1',
              prediction.severity === 'critical' && 'bg-accent-100 text-accent-600',
              prediction.severity === 'high' && 'bg-orange-100 text-orange-600',
              prediction.severity === 'medium' && 'bg-yellow-100 text-yellow-700'
            )}>
              {prediction.severity.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-ink-100">
              <div
                className="h-full bg-accent-500 transition-all"
                style={{ width: `${prediction.probability}%` }}
              ></div>
            </div>
            <span className="text-xs font-mono font-bold text-ink-700">{prediction.probability}%</span>
          </div>
        </div>
      ))}
    </div>

    <div className="flex items-center justify-between pt-2 border-t border-ink-100">
      <span className="text-xs font-mono uppercase tracking-wider text-ink-400">3 Active Predictions</span>
      <button className="text-xs font-mono text-accent-500 hover:text-accent-600 flex items-center gap-1">
        View All <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  </div>
);

const MapMockup = () => (
  <div className="bg-ocean-900 border-2 border-ink-200 p-4 relative overflow-hidden h-64">
    {/* Grid overlay */}
    <div className="absolute inset-0 opacity-20">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="absolute border-ocean-700" style={{
          left: `${i * 10}%`,
          top: 0,
          bottom: 0,
          borderLeftWidth: '1px'
        }}></div>
      ))}
      {[...Array(6)].map((_, i) => (
        <div key={i} className="absolute border-ocean-700" style={{
          top: `${i * 20}%`,
          left: 0,
          right: 0,
          borderTopWidth: '1px'
        }}></div>
      ))}
    </div>

    {/* Crisis markers */}
    <div className="absolute" style={{ top: '30%', left: '70%' }}>
      <div className="relative">
        <div className="w-6 h-6 bg-accent-500 animate-pulse"></div>
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white px-2 py-1 text-xs font-mono whitespace-nowrap border border-ink-200">
          Bangladesh
        </div>
      </div>
    </div>

    <div className="absolute" style={{ top: '50%', left: '55%' }}>
      <div className="relative">
        <div className="w-5 h-5 bg-orange-500 animate-pulse"></div>
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white px-2 py-1 text-xs font-mono whitespace-nowrap border border-ink-200">
          Kenya
        </div>
      </div>
    </div>

    <div className="absolute" style={{ top: '45%', left: '50%' }}>
      <div className="relative">
        <div className="w-7 h-7 bg-red-500 animate-pulse"></div>
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-white px-2 py-1 text-xs font-mono whitespace-nowrap border border-ink-200">
          Ethiopia
        </div>
      </div>
    </div>

    {/* Legend */}
    <div className="absolute bottom-4 left-4 bg-white/90 border border-ink-200 p-2">
      <p className="text-xs font-mono uppercase tracking-wider text-ink-600 mb-2">Severity</p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500"></div>
          <span className="text-xs text-ink-600">Critical</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-500"></div>
          <span className="text-xs text-ink-600">High</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-500"></div>
          <span className="text-xs text-ink-600">Medium</span>
        </div>
      </div>
    </div>

    {/* Coordinates */}
    <div className="absolute top-4 right-4 text-xs font-mono text-ocean-300">
      8.5N, 39.4E
    </div>
  </div>
);

const AnalyticsMockup = () => (
  <div className="bg-white border-2 border-ink-200 p-4 space-y-4">
    <div className="flex items-center justify-between border-b border-ink-100 pb-3">
      <span className="text-xs font-mono uppercase tracking-wider text-ink-500">Data Sources</span>
      <span className="text-xs font-mono text-sage-600">5 Active Feeds</span>
    </div>

    <div className="space-y-3">
      {[
        { name: 'Satellite Imagery', status: 'active', points: '2.4M', icon: Satellite },
        { name: 'Weather Stations', status: 'active', points: '156K', icon: Activity },
        { name: 'Field Reports', status: 'active', points: '8.2K', icon: FileText },
        { name: 'Social Media', status: 'delayed', points: '45K', icon: Radio },
        { name: 'Partner Agencies', status: 'active', points: '12K', icon: Users }
      ].map((source, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-ink-50 last:border-0">
          <div className="flex items-center gap-3">
            <source.icon className="w-4 h-4 text-ink-400" />
            <span className="text-sm text-ink-700">{source.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-ink-500">{source.points} pts</span>
            <div className={cn(
              'w-2 h-2',
              source.status === 'active' ? 'bg-sage-500' : 'bg-yellow-500'
            )}></div>
          </div>
        </div>
      ))}
    </div>

    {/* Bar chart visualization */}
    <div className="pt-4 border-t border-ink-100">
      <p className="text-xs font-mono uppercase tracking-wider text-ink-500 mb-3">Data Volume (24h)</p>
      <div className="flex items-end justify-between h-24 gap-1">
        {[65, 45, 80, 90, 70, 85, 95, 60, 75, 88, 92, 78].map((height, i) => (
          <div
            key={i}
            className="flex-1 bg-ocean-500 hover:bg-accent-500 transition-colors"
            style={{ height: `${height}%` }}
          ></div>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs font-mono text-ink-400">
        <span>00:00</span>
        <span>12:00</span>
        <span>24:00</span>
      </div>
    </div>
  </div>
);

const AlertsMockup = () => (
  <div className="bg-white border-2 border-ink-200 p-4 space-y-4">
    <div className="flex items-center gap-2 border-b border-ink-100 pb-3">
      <AlertTriangle className="w-4 h-4 text-accent-500" />
      <span className="text-xs font-mono uppercase tracking-wider text-ink-500">Active Alerts</span>
    </div>

    <div className="space-y-3">
      {[
        { title: 'Critical: Flood Warning', time: '2 min ago', type: 'critical', read: false },
        { title: 'High: Supply Shortage', time: '15 min ago', type: 'high', read: false },
        { title: 'Medium: Weather Update', time: '1 hour ago', type: 'medium', read: true },
        { title: 'Update: Team Deployed', time: '2 hours ago', type: 'info', read: true }
      ].map((alert, i) => (
        <div key={i} className={cn(
          'flex items-start gap-3 p-3 border-l-4 transition-colors',
          alert.type === 'critical' && 'border-l-accent-500 bg-accent-50',
          alert.type === 'high' && 'border-l-orange-500 bg-orange-50',
          alert.type === 'medium' && 'border-l-yellow-500 bg-yellow-50',
          alert.type === 'info' && 'border-l-ocean-500 bg-ocean-50',
          !alert.read && 'font-medium'
        )}>
          <div className="flex-1">
            <p className="text-sm text-ink-800">{alert.title}</p>
            <p className="text-xs text-ink-500 mt-1">{alert.time}</p>
          </div>
          {!alert.read && (
            <div className="w-2 h-2 bg-accent-500 mt-1"></div>
          )}
        </div>
      ))}
    </div>

    <button className="w-full py-2 border border-ink-200 text-xs font-mono uppercase tracking-wider text-ink-600 hover:bg-ink-50 transition-colors">
      View All Alerts
    </button>
  </div>
);

const CoordinationMockup = () => (
  <div className="bg-white border-2 border-ink-200 p-4 space-y-4">
    <div className="flex items-center justify-between border-b border-ink-100 pb-3">
      <span className="text-xs font-mono uppercase tracking-wider text-ink-500">Resource Allocation</span>
      <span className="text-xs font-mono text-accent-600">3 Pending</span>
    </div>

    <div className="space-y-3">
      {[
        { resource: 'Emergency Shelter Kits', qty: '5,000 units', location: 'Cox\'s Bazar', urgency: 'immediate' },
        { resource: 'ORS Packets', qty: '20,000 packets', location: 'Dadaab', urgency: '24h' },
        { resource: 'High Energy Biscuits', qty: '50 MT', location: 'Tigray', urgency: '72h' }
      ].map((item, i) => (
        <div key={i} className="border border-ink-100 p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="font-medium text-sm text-ink-800">{item.resource}</p>
              <p className="text-xs text-ink-500">{item.qty}</p>
            </div>
            <span className={cn(
              'text-xs font-mono px-2 py-1',
              item.urgency === 'immediate' && 'bg-accent-100 text-accent-600',
              item.urgency === '24h' && 'bg-orange-100 text-orange-600',
              item.urgency === '72h' && 'bg-yellow-100 text-yellow-700'
            )}>
              {item.urgency.toUpperCase()}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <MapPin className="w-3 h-3" />
            {item.location}
          </div>
        </div>
      ))}
    </div>

    {/* Agency coordination */}
    <div className="pt-4 border-t border-ink-100">
      <p className="text-xs font-mono uppercase tracking-wider text-ink-500 mb-3">Responding Agencies</p>
      <div className="flex items-center gap-2 flex-wrap">
        {['UNHCR', 'WFP', 'UNICEF', 'MSF', 'Red Cross'].map((agency, i) => (
          <span key={i} className="px-2 py-1 bg-ocean-50 text-ocean-700 text-xs font-mono">
            {agency}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const AuditMockup = () => (
  <div className="bg-white border-2 border-ink-200 p-4 space-y-4">
    <div className="flex items-center justify-between border-b border-ink-100 pb-3">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-sage-600" />
        <span className="text-xs font-mono uppercase tracking-wider text-ink-500">Decision Audit Trail</span>
      </div>
      <span className="text-xs font-mono text-sage-600">Verified</span>
    </div>

    <div className="space-y-0">
      {[
        { action: 'Resource Allocation Approved', user: 'Sarah Mitchell', time: '10:45 AM', verified: true },
        { action: 'Alert Escalated to Critical', user: 'John Doe', time: '10:30 AM', verified: true },
        { action: 'Field Team Dispatched', user: 'Maria Garcia', time: '10:15 AM', verified: true },
        { action: 'Crisis Assessment Complete', user: 'Ahmed Hassan', time: '09:45 AM', verified: true }
      ].map((item, i) => (
        <div key={i} className="flex items-start gap-3 py-3 border-l-2 border-ink-200 pl-4 relative">
          <div className="absolute -left-[5px] top-4 w-2 h-2 bg-sage-500"></div>
          <div className="flex-1">
            <p className="text-sm text-ink-800">{item.action}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-ink-500">{item.user}</span>
              <span className="text-xs text-ink-300">-</span>
              <span className="text-xs text-ink-500">{item.time}</span>
            </div>
          </div>
          {item.verified && (
            <CheckCircle className="w-4 h-4 text-sage-500 flex-shrink-0" />
          )}
        </div>
      ))}
    </div>

    <div className="pt-2 border-t border-ink-100 flex items-center justify-between">
      <span className="text-xs font-mono text-ink-400">Hash: 0x8f4a...3b2c</span>
      <button className="text-xs font-mono text-accent-500 hover:text-accent-600">
        Export Report
      </button>
    </div>
  </div>
);

const getMockup = (type: Feature['mockupType']) => {
  switch (type) {
    case 'dashboard': return <DashboardMockup />;
    case 'map': return <MapMockup />;
    case 'analytics': return <AnalyticsMockup />;
    case 'alerts': return <AlertsMockup />;
    case 'coordination': return <CoordinationMockup />;
    case 'audit': return <AuditMockup />;
    default: return <DashboardMockup />;
  }
};

export default function ProductDetails() {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState<string>(features[0].id);

  const stats = [
    { label: 'Crises Predicted', value: '2,400+', icon: Brain },
    { label: 'People Protected', value: '12M+', icon: Users },
    { label: 'Response Time', value: '-48hrs', icon: Clock },
    { label: 'Data Sources', value: '150+', icon: Database }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-ink-900 text-white">
        <div className="container-grid py-16 md:py-24">
          <div className="grid-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="text-xs font-mono uppercase tracking-ultra text-accent-400 mb-4">
                Humanitarian Decision Support Platform
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium leading-tight mb-6">
                Anticipate. Respond. <span className="text-accent-400">Save Lives.</span>
              </h1>
              <p className="text-lg text-ink-300 mb-8 max-w-lg">
                CLEAR combines AI-powered predictions, real-time data integration, and intelligent
                resource allocation to help humanitarian organizations respond faster and more effectively.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/demos')}
                  className="btn bg-accent-500 text-white px-6 py-3 hover:bg-accent-600 transition-colors flex items-center gap-2"
                >
                  Try Demo <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn border-2 border-ink-600 text-white px-6 py-3 hover:border-white transition-colors"
                >
                  Explore Features
                </button>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-6 mt-12 lg:mt-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-500/20 to-ocean-500/20 -rotate-3"></div>
                <DashboardMockup />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-b-2 border-ink-200 bg-white">
        <div className="container-grid py-12">
          <div className="grid-12">
            {stats.map((stat, i) => (
              <div key={i} className="col-span-6 md:col-span-3 text-center py-4">
                <stat.icon className="w-6 h-6 text-accent-500 mx-auto mb-3" />
                <p className="text-2xl md:text-3xl font-display font-medium text-ink-900">{stat.value}</p>
                <p className="text-xs font-mono uppercase tracking-ultra text-ink-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section id="features" className="py-16 md:py-24">
        <div className="container-grid">
          <div className="grid-12 mb-12">
            <div className="col-span-12 text-center">
              <p className="text-xs font-mono uppercase tracking-ultra text-accent-500 mb-4">
                Platform Capabilities
              </p>
              <h2 className="text-2xl md:text-3xl font-display mb-4">
                Powerful Tools for Crisis Response
              </h2>
              <p className="text-ink-600 max-w-2xl mx-auto">
                Every feature is designed with humanitarian field workers in mind - intuitive interfaces,
                offline capability, and seamless collaboration across organizations.
              </p>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid-12 gap-y-4">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="col-span-12 md:col-span-6 lg:col-span-4"
              >
                <button
                  onClick={() => setActiveFeature(feature.id)}
                  className={cn(
                    'w-full text-left p-6 border-2 transition-all h-full',
                    activeFeature === feature.id
                      ? 'border-accent-500 bg-accent-50'
                      : 'border-ink-200 bg-white hover:border-ink-300'
                  )}
                >
                  <feature.icon className={cn(
                    'w-8 h-8 mb-4',
                    activeFeature === feature.id ? 'text-accent-500' : 'text-ink-400'
                  )} />
                  <h3 className="text-lg font-medium text-ink-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-ink-600 line-clamp-2">{feature.description}</p>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Detail Sections */}
      {features.map((feature, index) => (
        <section
          key={feature.id}
          id={feature.id}
          className={cn(
            'py-16 md:py-24',
            index % 2 === 0 ? 'bg-white' : 'bg-sand-100'
          )}
        >
          <div className="container-grid">
            <div className="grid-12 items-center">
              <div className={cn(
                'col-span-12 lg:col-span-5',
                index % 2 === 1 && 'lg:order-2'
              )}>
                <div className="flex items-center gap-3 mb-4">
                  <feature.icon className="w-8 h-8 text-accent-500" />
                  <p className="text-xs font-mono uppercase tracking-ultra text-accent-500">
                    Feature {index + 1}
                  </p>
                </div>
                <h2 className="text-2xl md:text-3xl font-display mb-4">{feature.title}</h2>
                <p className="text-ink-600 mb-6">{feature.description}</p>

                <ul className="space-y-3 mb-8">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-500 flex-shrink-0 mt-0.5" />
                      <span className="text-ink-700">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/demos')}
                  className="text-accent-500 font-medium flex items-center gap-2 hover:gap-3 transition-all"
                >
                  See it in action <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div className={cn(
                'col-span-12 lg:col-span-6',
                index % 2 === 0 ? 'lg:col-start-7' : 'lg:col-start-1 lg:order-1'
              )}>
                <div className="mt-8 lg:mt-0">
                  {getMockup(feature.mockupType)}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Integration Section */}
      <section className="py-16 md:py-24 bg-ink-900 text-white">
        <div className="container-grid">
          <div className="grid-12">
            <div className="col-span-12 text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-ultra text-accent-400 mb-4">
                Integrations
              </p>
              <h2 className="text-2xl md:text-3xl font-display mb-4">
                Works With Your Existing Tools
              </h2>
              <p className="text-ink-400 max-w-2xl mx-auto">
                CLEAR integrates seamlessly with major humanitarian data systems and coordination platforms.
              </p>
            </div>

            <div className="col-span-12">
              <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                {[
                  'UN OCHA', 'HDX', 'UNHCR', 'WFP', 'ReliefWeb',
                  'ACAPS', 'IOM DTM', 'WHO', 'FEWS NET', 'GDACS'
                ].map((partner, i) => (
                  <div
                    key={i}
                    className="px-6 py-3 border border-ink-700 text-ink-300 text-sm font-mono uppercase tracking-wider hover:border-accent-500 hover:text-accent-400 transition-colors"
                  >
                    {partner}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container-grid">
          <div className="grid-12">
            <div className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3 text-center">
              <h2 className="text-2xl md:text-3xl font-display mb-4">
                Ready to Transform Your Crisis Response?
              </h2>
              <p className="text-ink-600 mb-8 max-w-xl mx-auto">
                Join humanitarian organizations worldwide using CLEAR to anticipate crises
                and coordinate more effective responses.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => navigate('/demos')}
                  className="btn bg-accent-500 text-white px-8 py-3 hover:bg-accent-600 transition-colors flex items-center gap-2"
                >
                  Explore the Platform <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="mailto:clear@nrc.no"
                  className="btn border-2 border-ink-300 text-ink-700 px-8 py-3 hover:border-ink-500 transition-colors no-underline"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
